'use client';

import { useGuestOnly } from '@/hooks';
import { useSocialAuth } from '@/hooks/use-social-auth';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { setNewUserFlag } from '@/shared/lib/auth/new-user-flag';
import { getOnboardingRedirectPath, isProfileComplete } from '@/shared/lib/auth/profile-completion';
import { setRegisterFlowToken } from '@/shared/lib/auth/register-flow-guard';
import { applyPendingRedeemCode, saveRedeemCode } from '@/shared/lib/redeem/apply-pending-code';
import { useAuthStore } from '@/shared/stores/auth-store';
import { TwoFactorChallenge } from '@/components/auth/two-factor-challenge';
import { getSafeRedirectPath } from '@/shared/lib/routes/safe-redirect';
import { restoreEmailPlus } from '@/shared/utils/url-params';
import { Loader2 } from 'lucide-react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import posthog from 'posthog-js';
import { Suspense, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { OAuthRow } from './_components/oauth-row';
import { OtpCodeForm } from './_components/otp-code-form';
import { OtpCodeFormSkeleton } from './_components/otp-code-form-skeleton';
import { OtpEmailForm } from './_components/otp-email-form';
import { PasswordForm } from './_components/password-form';
import { sendOtpForSignIn } from './_lib/send-otp';
import { resolvePostLoginPath } from './_lib/post-login-path';

type Flow = 'password' | 'otp';
type OtpStep = 'form' | 'code';
type SignInMethod = 'email' | 'google' | 'apple' | 'otp';

function SignInContent() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const subscriptionActivated = searchParams.get('subscription_activated') === 'true';
  const prefillEmail = restoreEmailPlus(searchParams.get('email') || '');
  const redeemCode = searchParams.get('redeem_code');
  const redirectTo = getSafeRedirectPath(searchParams.get('redirect'));
  const showDiscordLogin = searchParams.get('discord') === 'true';
  const { login, isLoading, clearError } = useGuestOnly('/');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const {
    signInWithGoogle,
    signInWithApple,
    isLoading: socialLoading,
    error: socialError,
  } = useSocialAuth();
  const [email, setEmail] = useState(prefillEmail);
  const [flow, setFlow] = useState<Flow>(() => (prefillEmail ? 'otp' : 'password'));
  const [otpStep, setOtpStep] = useState<OtpStep>('form');
  const [autoSubmitting, setAutoSubmitting] = useState<boolean>(!!prefillEmail);
  const autoFiredRef = useRef(false);
  // Challenge token stays in React state for the life of this screen only.
  const [challenge, setChallenge] = useState<{ token: string; method: SignInMethod } | null>(null);

  const isAnyLoading = isLoading || socialLoading;

  // Auto-progress past the email-entry step when the LP forwarder hands us
  // ?email=. Mirrors choose-plan-content.tsx (computeInitialStep + auto-fire
  // useEffect): one-shot per mount, bails for already-authenticated users so
  // we never send an OTP for someone useGuestOnly is about to bounce.
  //
  // We do NOT gate on isInitialized because checkAuth() is only invoked from
  // the persist onRehydrateStorage callback, which bails when localStorage is
  // empty — so for fresh guests isInitialized stays false forever and the
  // spinner would hang. isAuthenticated defaults to false from create() and
  // is set optimistically true on rehydrate when a user exists, so it is a
  // reliable signal at first render.
  //
  // ?email= is stripped via replaceState after consume to keep PII out of the
  // URL bar and prevent re-fire on back-nav / refresh.
  useEffect(() => {
    if (!prefillEmail) return;
    if (isAuthenticated) {
      setAutoSubmitting(false);
      return;
    }
    if (autoFiredRef.current) return;
    autoFiredRef.current = true;

    (async () => {
      const result = await sendOtpForSignIn(prefillEmail);

      if (result.kind === 'bounced') {
        toast.info('No active subscription on this email. Choose a plan to get started.');
        router.push(result.redirectTo);
        return;
      }
      if (result.kind === 'sent') {
        const next = new URLSearchParams(searchParams.toString());
        next.delete('email');
        const query = next.toString();
        window.history.replaceState({}, '', query ? `${pathname}?${query}` : pathname);
        setOtpStep('code');
        setAutoSubmitting(false);
        return;
      }
      if (result.kind === 'rate_limited') {
        toast.error('Too many attempts. Please wait a moment and try again.');
        setAutoSubmitting(false);
        return;
      }
      toast.error(result.message);
      setAutoSubmitting(false);
    })();
  }, [prefillEmail, isAuthenticated, router, searchParams, pathname]);

  /**
   * Navigate immediately after login, then fire analytics/redeem in background.
   * This removes ~500ms of blocking work from the critical path.
   */
  const navigateAndDeferPostLogin = (method: SignInMethod) => {
    const { user, isSubscribed } = useAuthStore.getState();
    const onboardingPath = getOnboardingRedirectPath(user, isSubscribed);

    // Social auth can create a new user (Firebase login-or-register).
    // Flag it so onboarding_profile_completed picks up is_new_user correctly.
    if (!isProfileComplete(user)) setNewUserFlag();

    // Send analytics before navigation — window.location.replace tears down the page
    toast.success('Welcome back!');
    posthog.capture(
      AnalyticsEvent.AUTH_LOGIN_COMPLETED,
      { method, is_new_user: false },
      { send_instantly: true }
    );

    window.location.replace(resolvePostLoginPath(onboardingPath, redirectTo));
    applyPendingRedeemCode(searchParams).then((redeemResult) => {
      if (redeemResult.applied && redeemResult.success) {
        useAuthStore.getState().setSubscribed(true);
        toast.success(redeemResult.message || 'Subscription activated!');
      } else if (redeemResult.applied && !redeemResult.success) {
        toast.error(redeemResult.message);
      }
    });
  };

  const handleGoogleSignIn = async () => {
    const result = await signInWithGoogle();
    if (result.twoFactorRequired && result.challengeToken) {
      setChallenge({ token: result.challengeToken, method: 'google' });
      return;
    }
    if (result.success) {
      navigateAndDeferPostLogin('google');
    } else if (result.error && result.error !== 'Sign-in cancelled') {
      posthog.capture(AnalyticsEvent.AUTH_LOGIN_FAILED, {
        method: 'google',
        error_type: 'social_error',
      });
      toast.error(result.error);
    }
  };

  const handleAppleSignIn = async () => {
    const result = await signInWithApple();
    if (result.twoFactorRequired && result.challengeToken) {
      setChallenge({ token: result.challengeToken, method: 'apple' });
      return;
    }
    if (result.success) {
      navigateAndDeferPostLogin('apple');
    } else if (result.error && result.error !== 'Sign-in cancelled') {
      posthog.capture(AnalyticsEvent.AUTH_LOGIN_FAILED, {
        method: 'apple',
        error_type: 'social_error',
      });
      toast.error(result.error);
    }
  };

  const handleGetStarted = () => {
    if (redeemCode) {
      setRegisterFlowToken();
      saveRedeemCode(redeemCode);
      router.push(`/register?redeem_code=${encodeURIComponent(redeemCode)}`);
    } else {
      router.push('/choose-plan');
    }
  };

  // Flow-toggle handlers — capture an explicit preference signal each time
  // the user swaps. Email presence is included so we can segment users who
  // switched mid-flow (already typing) vs those who swapped on arrival.
  const handleSwitchToOtp = () => {
    posthog.capture(AnalyticsEvent.AUTH_SIGN_IN_SWITCHED_TO_OTP, {
      email_present: !!email,
    });
    setFlow('otp');
  };

  const handleSwitchToPassword = () => {
    posthog.capture(AnalyticsEvent.AUTH_SIGN_IN_SWITCHED_TO_PASSWORD, {
      email_present: !!email,
    });
    setFlow('password');
  };

  // Discord login starts a redirect flow: navigate to the BE /auth/discord/redirect
  // endpoint, which 302-redirects to Discord's OAuth consent screen. Discord posts
  // back to the configured DISCORD_REDIRECT_URI (a FE callback page, separate work).
  const handleDiscordSignIn = () => {
    posthog.capture(AnalyticsEvent.AUTH_DISCORD_REDIRECT_INITIATED, {}, { send_instantly: true });
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'https://api.taboo.tv/api';
    window.location.href = `${apiUrl}/auth/discord/redirect`;
  };

  return (
    <div className="w-full">
      {/* Subscription Activation Banner */}
      {subscriptionActivated && (
        <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
          <p className="text-sm text-green-500 text-center font-medium">
            Your subscription has been activated! Sign in to start watching.
          </p>
        </div>
      )}

      {/* Redeem Code Banner */}
      {redeemCode && (
        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <p className="text-sm text-amber-400 text-center font-medium">
            Sign in to redeem your code
          </p>
        </div>
      )}

      {/* Header — the challenge brings its own */}
      {!challenge && (
        <div className="text-left mb-5">
          <h1 className="text-[28px] font-bold leading-[1.15] tracking-[-0.02em] text-white">
            Endless access starts here
          </h1>
          <p className="mt-1.5 text-sm text-text-secondary">Sign in or create an account.</p>
        </div>
      )}

      {challenge && (
        <TwoFactorChallenge
          challengeToken={challenge.token}
          method={challenge.method}
          {...(email ? { email } : {})}
          onSuccess={() => navigateAndDeferPostLogin(challenge.method)}
          onRestart={() => setChallenge(null)}
        />
      )}

      {!challenge && autoSubmitting ? (
        <OtpCodeFormSkeleton />
      ) : !challenge && otpStep === 'code' ? (
        <OtpCodeForm
          email={email}
          onBack={() => setOtpStep('form')}
          onSuccess={() => navigateAndDeferPostLogin('otp')}
          onTwoFactor={(token) => setChallenge({ token, method: 'otp' })}
        />
      ) : !challenge && flow === 'password' ? (
        <PasswordForm
          email={email}
          setEmail={setEmail}
          isAnyLoading={isAnyLoading}
          isLoading={isLoading}
          login={login}
          clearError={clearError}
          onSuccess={() => navigateAndDeferPostLogin('email')}
          onTwoFactor={(token) => setChallenge({ token, method: 'email' })}
          onSwitchToOtp={handleSwitchToOtp}
        />
      ) : !challenge ? (
        <OtpEmailForm
          email={email}
          setEmail={setEmail}
          isAnyLoading={isAnyLoading}
          onCodeSent={() => setOtpStep('code')}
          onSwitchToPassword={handleSwitchToPassword}
        />
      ) : null}

      {!challenge && !autoSubmitting && otpStep === 'form' && (
        <OAuthRow
          isAnyLoading={isAnyLoading}
          socialLoading={socialLoading}
          socialError={socialError}
          onGoogleSignIn={handleGoogleSignIn}
          onAppleSignIn={handleAppleSignIn}
          {...(showDiscordLogin ? { onDiscordSignIn: handleDiscordSignIn } : {})}
        />
      )}

      {!challenge && !autoSubmitting && otpStep === 'form' && (
        <p className="mt-[18px] text-center text-[13px] text-text-secondary">
          Don&apos;t have an account?{' '}
          <button
            type="button"
            onClick={handleGetStarted}
            className="font-medium text-red-primary transition-colors hover:text-red-hover hover:underline underline-offset-2 cursor-pointer"
          >
            Get started
          </button>
        </p>
      )}
    </div>
  );
}

export default function SignInPage() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-red-primary" />
        </div>
      }
    >
      <SignInContent />
    </Suspense>
  );
}
